<template>
  <header
    :class="headerClasses"
    v-bind="aosAttributes"
  >
    <div class="global-header__shell">
      <div class="global-header__bar">
        <RouterLink to="/" class="global-header__logo" title="BioEdge">
          <img
            class="global-header__logo-image"
            src="https://firebasestorage.googleapis.com/v0/b/bio-edge.firebasestorage.app/o/ChatGPT%20Image%20Oct%2023%2C%202025%2C%2009_28_18%20PM.png?alt=media&token=58763c87-6345-4065-858e-3093a8fcca6d"
            alt="BioEdge Logo"
          />
        </RouterLink>

        <div class="global-header__nav">
          <nav class="global-header__links">
            <RouterLink
              v-for="item in navItems"
              :key="item.label"
              :to="item.to"
              class="global-header__link"
            >
              {{ item.label }}
            </RouterLink>
          </nav>

          <div class="global-header__actions">
            <slot name="actions" />
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { computed, reactive } from 'vue'
import { RouterLink } from 'vue-router'

export default {
  name: 'GlobalHeader',
  components: { RouterLink },
  props: {
    mode: {
      type: String,
      default: 'default'
    },
    ctaTo: {
      type: [String, Object],
      default: () => ({ path: '/products', hash: '#catalog' })
    },
    useAos: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const navItems = reactive([
      { label: 'Home', to: '/' },
      { label: 'Products', to: '/products' }
    ])

    const headerClasses = computed(() => [
      'global-header',
      props.mode === 'overlay' ? 'global-header--overlay' : 'global-header--default'
    ])

    const aosAttributes = computed(() => {
      if (!props.useAos) {
        return {}
      }
      return {
        'data-aos': 'fade-up',
        'data-aos-duration': '1000'
      }
    })

    return {
      navItems,
      headerClasses,
      aosAttributes
    }
  }
}
</script>

<style scoped>
.global-header {
  width: 100%;
  z-index: 30;
  padding: 1.75rem 0;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

.global-header--overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    180deg,
    rgba(6, 18, 3, 0.92) 0%,
    rgba(6, 18, 3, 0.75) 55%,
    rgba(6, 18, 3, 0) 100%
  );
}

.global-header--default {
  position: sticky;
  top: 0;
  background: rgba(6, 18, 3, 0.96);
  backdrop-filter: blur(12px);
  box-shadow: 0 18px 36px rgba(6, 18, 3, 0.28);
}

.global-header__shell {
  width: 100%;
  max-width: none;
  padding-left: clamp(1rem, 4vw, 4.5rem);
  padding-right: clamp(1rem, 3vw, 3rem);
}

.global-header__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.global-header__logo {
  display: inline-flex;
  align-items: center;
  border-radius: 0.75rem;
  padding: 0.25rem;
  background: rgba(6, 18, 3, 0.35);
  backdrop-filter: blur(10px);
  transition: transform 0.25s ease;
}

.global-header__logo:hover {
  transform: translateY(-2px);
}

.global-header__logo-image {
  height: 3.5rem;
  width: auto;
}

.global-header__nav {
  display: flex;
  align-items: center;
  gap: clamp(1.25rem, 3vw, 2.8rem);
}

.global-header__links {
  display: flex;
  align-items: center;
  gap: clamp(1.25rem, 3vw, 2.6rem);
  font-family: 'Montserrat', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
}

.global-header__link {
  position: relative;
  color: inherit;
  transition: color 0.25s ease;
}

.global-header__link::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -0.35rem;
  height: 2px;
  background: rgba(255, 255, 255, 0.85);
  transform: scaleX(0);
  opacity: 0;
  transform-origin: center;
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.global-header__link:hover,
.global-header__link:focus {
  color: #52cd36;
}

.global-header__link:hover::after,
.global-header__link:focus::after {
  opacity: 1;
  transform: scaleX(1);
  background: #52cd36;
}

.global-header__actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.global-header__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.65rem 1.8rem;
  border-radius: 9999px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.05em;
  background: linear-gradient(135deg, #52cd36, #46b42f);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0 20px 40px rgba(82, 205, 54, 0.32);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.global-header__cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 26px 52px rgba(82, 205, 54, 0.4);
}

:deep(.global-header__cart-btn) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.6rem;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(6, 18, 3, 0.4);
  color: #ffffff;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  letter-spacing: 0.04em;
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

:deep(.global-header__cart-btn:hover),
:deep(.global-header__cart-btn:focus) {
  background: rgba(6, 18, 3, 0.6);
  border-color: rgba(82, 205, 54, 0.6);
  transform: translateY(-2px);
}

:deep(.global-header__cart-icon) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.4rem;
}

:deep(.global-header__cart-icon svg) {
  width: 1.1rem;
  height: 1.1rem;
  fill: currentColor;
}

@media (max-width: 1024px) {
  .global-header__nav {
    display: none;
  }
}
</style>
